import { useEffect, useState } from 'react';
import baseURL from './url';

const DEFAULT_TTL = 5 * 60 * 1000;
const CACHE_PREFIX = 'mujermia-cache:';
const memoryCache = new Map();
const pendingRequests = new Map();

const resourceConfig = {
    tienda: {
        endpoint: 'tiendaGet.php',
        transform: (data) => [...(data?.tienda || [])].reverse(),
    },
    banners: {
        endpoint: 'bannersGet.php',
        transform: (data) => [...(data?.banner || [])].reverse(),
    },
    productos: {
        endpoint: 'productosGet.php',
        transform: (data) => data?.productos || [],
    },
    secciones: {
        endpoint: 'seccionesGet.php',
        transform: (data) => [...(data?.secciones || [])].sort((a, b) => Number(a.orden || 0) - Number(b.orden || 0)),
    },
    categorias: {
        endpoint: 'categoriasGet.php',
        transform: (data) => [...(data?.categorias || [])].sort((a, b) => Number(a.ordenSeccion || 0) - Number(b.ordenSeccion || 0) || Number(a.orden || 0) - Number(b.orden || 0)),
    },
    subcategorias: {
        endpoint: 'subCategoriaGet.php',
        transform: (data) => data?.subcategorias || [],
    },
    metodos: {
        endpoint: 'metodoGet.php',
        transform: (data) => data?.metodos || [],
    },
    estado: {
        endpoint: 'estado.php',
        transform: (data) => data?.estado || null,
    },
};

function now() {
    return Date.now();
}

function cacheKey(key) {
    return `${CACHE_PREFIX}${key}`;
}

function readStored(key) {
    try {
        const stored = sessionStorage.getItem(cacheKey(key));
        if (!stored) return null;

        const parsed = JSON.parse(stored);
        if (!parsed || parsed.expiresAt < now()) {
            sessionStorage.removeItem(cacheKey(key));
            return null;
        }

        return parsed.data;
    } catch (error) {
        return null;
    }
}

function writeStored(key, data, ttl = DEFAULT_TTL) {
    const record = {
        data,
        expiresAt: now() + ttl,
    };

    memoryCache.set(key, record);

    try {
        sessionStorage.setItem(cacheKey(key), JSON.stringify(record));
    } catch (error) {
        // Session storage can be unavailable in private browsing.
    }
}

export function getCachedResource(key) {
    const record = memoryCache.get(key);
    if (record && record.expiresAt >= now()) {
        return record.data;
    }

    return readStored(key);
}

export function clearResourceCache(key) {
    memoryCache.delete(key);
    pendingRequests.delete(key);

    try {
        sessionStorage.removeItem(cacheKey(key));
    } catch (error) {
        // Ignore storage cleanup errors.
    }
}

export function loadResource(key, options = {}) {
    const config = resourceConfig[key];
    if (!config) {
        return Promise.reject(new Error(`Recurso no configurado: ${key}`));
    }

    const cached = getCachedResource(key);
    if (cached && !options.force) {
        return Promise.resolve(cached);
    }

    if (pendingRequests.has(key) && !options.force) {
        return pendingRequests.get(key);
    }

    const request = fetch(`${baseURL}/${config.endpoint}`, { method: 'GET' })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`No se pudo cargar ${config.endpoint}`);
            }
            return response.json();
        })
        .then((data) => {
            const transformed = config.transform(data);
            writeStored(key, transformed, options.ttl || DEFAULT_TTL);
            return transformed;
        })
        .finally(() => {
            pendingRequests.delete(key);
        });

    pendingRequests.set(key, request);
    return request;
}

export function useStoreResource(key, fallback) {
    const cached = getCachedResource(key);
    const [data, setData] = useState(cached ?? fallback);
    const [loading, setLoading] = useState(!cached);

    useEffect(() => {
        let active = true;

        loadResource(key)
            .then((result) => {
                if (!active) return;
                setData(result);
                setLoading(false);
            })
            .catch((error) => {
                console.error(`Error al cargar ${key}:`, error);
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [key]);

    return { data, loading };
}

export function useTiendaList() {
    return useStoreResource('tienda', []);
}

export function useTiendaPrincipal() {
    const { data, loading } = useTiendaList();
    return {
        tienda: data?.[0] || null,
        tiendaList: data || [],
        loading,
    };
}

export function useBanners() {
    return useStoreResource('banners', []);
}
